import type { App } from "../types/platform";
import { APP_STATUS } from "../data/status";
import { B2PROC_ANGLE, LAYER_RADII, posAtAngle } from "../hooks/useActiveNode";
import { StatusMark } from "./StatusMark";

export function ProofNode({
  app,
  isActive,
  isPinned,
  isConnected,
  onHover,
  onClick
}: {
  app: App;
  isActive: boolean;
  isPinned: boolean;
  isConnected: boolean;
  onHover: (app: App) => void;
  onClick: (app: App) => void;
}) {
  const p = posAtAngle(B2PROC_ANGLE, LAYER_RADII.b2proc);

  const bg = isActive
    ? "#fff"
    : isConnected
      ? "rgba(242,129,50,0.18)"
      : "rgba(242,129,50,0.10)";
  const borderOpacity = isActive ? 0.95 : isConnected ? 0.85 : 0.6;
  const shadow = isActive
    ? "0 0 44px rgba(242,129,50,0.55)"
    : isConnected
      ? "0 0 32px rgba(242,129,50,0.40)"
      : "0 0 18px rgba(242,129,50,0.18)";
  const labelColor = isActive ? "#F28132" : "#fff";
  const status = APP_STATUS[app.status];
  const statusColor = isActive
    ? "#F28132"
    : isConnected
      ? "rgba(242,129,50,0.60)"
      : "rgba(255,255,255,0.35)";

  return (
    <button
      type="button"
      aria-label={`B2proC: ${app.name}, ${app.tagline} Status: ${status.label}.`}
      aria-pressed={isPinned}
      onMouseEnter={() => onHover(app)}
      onFocus={() => onHover(app)}
      onClick={() => onClick(app)}
      className="absolute outline-none"
      style={{
        left: `${p.x}%`,
        top: `${p.y}%`,
        transform: "translate(-50%, -50%)"
      }}
    >
      <div
        className="relative flex flex-col items-center transition-transform duration-300"
        style={{ transform: isActive ? "scale(1.10)" : "scale(1)" }}
      >
        <StatusMark
          line={status.line}
          diameter={72}
          color={statusColor}
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transition-colors duration-300"
        />
        <div
          className="flex items-center justify-center rounded-full transition-all duration-300"
          style={{
            width: 64,
            height: 64,
            backgroundColor: bg,
            border: `1.5px solid rgba(242,129,50,${borderOpacity})`,
            boxShadow: shadow,
            backdropFilter: "blur(8px)"
          }}
        >
          <div
            className="px-1 text-center text-[10px] tracking-tight transition-colors duration-300"
            style={{
              color: labelColor,
              fontWeight: 600,
              lineHeight: 1.1
            }}
          >
            {app.name}
          </div>
        </div>
      </div>
    </button>
  );
}
